/**
 * Utilitários de busca para mapeamento de planos.
 * Suporta regex (com escape de caracteres especiais para busca literal)
 * e ordenação por relevância (exato primeiro, depois por similaridade).
 */

import type { IAgreementErpPlan } from '@partiu/api/dashboard/types.ts';

/**
 * Retorna o valor correto para plan_slug_integration no payload do backend.
 * Quando o tipo de mapeamento é por ID (map_type.slug === 'ID-PLANO' ou 'ID_PLANO'),
 * envia o ID do plano ERP; caso contrário, envia o nome.
 */
export function getPlanSlugIntegrationForPayload(row: IAgreementErpPlan): string {
  const mappingSlug = (row.type_mapping ?? '').toString().toUpperCase().replace(/-/g, '_');
  if (mappingSlug === 'ID_PLANO') {
    return String(row.id ?? row.slug);
  }
  return row.name || row.slug || String(row.id);
}

const REGEX_SPECIAL_CHARS = /[.*+?^${}()|[\]\\]/g;

/**
 * Escapa caracteres especiais do regex para busca literal.
 * Ex: "26F (2L1R)" → "26F \\(2L1R\\)"
 */
function escapeRegexForLiteral(term: string): string {
  return term.replace(REGEX_SPECIAL_CHARS, '\\$&');
}

/**
 * Cria regex para busca. Por padrão usa modo literal (escape).
 * Se term contiver apenas caracteres seguros, permite busca flexível.
 */
function buildSearchRegex(term: string, flags = 'i'): RegExp | null {
  const trimmed = term.trim();
  if (!trimmed) return null;
  try {
    const escaped = escapeRegexForLiteral(trimmed);
    return new RegExp(escaped, flags);
  } catch {
    return null;
  }
}

export interface SearchableRow {
  id?: number | string | null;
  name?: string | null;
  slug?: string | null;
  count_clients_active?: number | null;
}

/**
 * Calcula score de relevância da busca (maior = mais relevante).
 * - 100: match exato (nome ou slug igual ao termo)
 * - 80: começa com o termo
 * - 60: match no início de palavra
 * - 40: contém o termo
 * - 0: não match
 */
export function scoreSearchMatch(
  row: SearchableRow,
  term: string,
  regex: RegExp | null
): number {
  if (!regex) return 0;

  const termLower = term.toLowerCase().trim();
  const name = (row.name ?? '').toLowerCase();
  const slug = (row.slug ?? '').toLowerCase();
  const idStr = row.id != null ? String(row.id).toLowerCase() : '';

  let bestScore = 0;

  for (const text of [name, slug]) {
    if (!text) continue;
    if (text === termLower) {
      bestScore = Math.max(bestScore, 100);
      break;
    }
    if (text.startsWith(termLower)) {
      bestScore = Math.max(bestScore, 80);
    }
    const match = text.match(regex);
    if (match) {
      const idx = match.index ?? -1;
      // Match no início ou após espaço (início de palavra)
      const atWordStart = idx === 0 || (idx > 0 && /\s/.test(text[idx - 1]));
      bestScore = Math.max(bestScore, atWordStart ? 60 : 40);
    }
  }

  if (idStr === termLower) {
    bestScore = Math.max(bestScore, 90);
  } else if (idStr.includes(termLower)) {
    bestScore = Math.max(bestScore, 30);
  }

  return bestScore;
}

/**
 * Verifica se a linha corresponde à busca (id, nome ou slug).
 */
export function matchesSearch(
  row: SearchableRow,
  term: string,
  regex: RegExp | null
): boolean {
  if (!term.trim() || !regex) return true;

  const termLower = term.toLowerCase().trim();
  const name = (row.name ?? '').toLowerCase();
  const slug = (row.slug ?? '').toLowerCase();
  const idStr = row.id != null ? String(row.id).toLowerCase() : '';

  return (
    regex.test(name) ||
    regex.test(slug) ||
    idStr.includes(termLower)
  );
}

/** Quando o termo é muito longo, exige match exato — senão retorna vazio. */
const LONG_SEARCH_THRESHOLD = 25;

/**
 * Verifica se nome ou slug é exatamente igual ao termo (case insensitive).
 */
function isExactMatch(row: SearchableRow, term: string): boolean {
  const termLower = term.toLowerCase().trim();
  const name = (row.name ?? '').toLowerCase().trim();
  const slug = (row.slug ?? '').toLowerCase().trim();
  return name === termLower || slug === termLower;
}

/**
 * Filtra e ordena linhas por relevância da busca.
 * Quando há busca: ordena por score (desc), depois por count_clients_active (desc).
 * Se o termo for muito longo: só aceita match exato, senão retorna vazio.
 */
export function filterAndSortBySearchRelevance<T extends SearchableRow>(
  rows: T[],
  searchTerm: string,
  sortByClientsActive = true
): T[] {
  const term = searchTerm.trim();
  if (!term) return rows;

  const regex = buildSearchRegex(term);
  if (!regex) return rows;

  const isLongSearch = term.length > LONG_SEARCH_THRESHOLD;

  const filtered = rows.filter((row) => {
    if (isLongSearch) {
      return isExactMatch(row, term);
    }
    return matchesSearch(row, term, regex);
  });

  return filtered.sort((a, b) => {
    const scoreA = scoreSearchMatch(a, term, regex);
    const scoreB = scoreSearchMatch(b, term, regex);

    if (scoreB !== scoreA) {
      return scoreB - scoreA;
    }
    if (sortByClientsActive) {
      const clientsA = a.count_clients_active ?? 0;
      const clientsB = b.count_clients_active ?? 0;
      return clientsB - clientsA;
    }
    return 0;
  });
}
